import { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "../utils/apiClient";
import { usePlan } from "../contexts/PlanContext";
import { createSoftphoneDriver } from "../utils/softphone";

/**
 * The browser softphone: registration lifecycle, active call state, and the
 * decision about whether this counsellor is in browser mode at all.
 *
 * Mounted once, globally, by the Softphone widget. It is deliberately the only
 * thing that talks to a driver — every consumer reads `status` and `activeCall`
 * and calls the returned actions.
 *
 * ── The rule this hook exists to enforce ──
 * **A softphone that fails closed loses the call; one that fails over just rings
 * a mobile.** Every failure path here — plan without telephony, provider without
 * WebRTC, no SIP identity, missing SDK, denied microphone, dropped registration,
 * offline — ends at `mode === 'phone'` with a `reason` the counsellor can read.
 * The dial button never stops working; only which device rings changes.
 */

/** Backoff for re-registration after a drop. Capped so it never stops trying. */
const RETRY_MS = [3000, 8000, 20_000, 45_000];

export default function useSoftphone() {
  const { hasPlanFeature } = usePlan();
  const planEnabled = hasPlanFeature("cloudTelephony");

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  // idle → registering → registered | failed | unavailable
  const [status, setStatus] = useState("idle");
  const [reason, setReason] = useState("");
  const [activeCall, setActiveCall] = useState(null);
  // Mirrors activeCall so the mute/hold actions can read it without putting a
  // driver call inside a state updater. See toggleMute for why that matters.
  const activeCallRef = useRef(null);

  const driverRef = useRef(null);
  const retryRef = useRef(null);
  const attemptRef = useRef(0);
  // Bumped by every register/unregister. Registration is a multi-step await
  // (capability check → microphone → SIP), and the counsellor can switch back to
  // phone mode in the middle of it. Without a generation to compare against, the
  // superseded attempt finishes into a hook that has already torn down — see the
  // teardown branch in register() for what that costs.
  const epochRef = useRef(0);
  // Registration is async and the component can unmount mid-flight; without this
  // a late callback resurrects state on a dead component.
  const aliveRef = useRef(true);

  useEffect(() => () => { aliveRef.current = false; }, []);

  useEffect(() => { activeCallRef.current = activeCall; }, [activeCall]);

  /** Whether this browser should be running a softphone right now. */
  const wantsWebrtc = !!config?.enabled && config?.mode === "webrtc";

  // ── Load the agent's own softphone config ─────────────────────────────
  const loadConfig = useCallback(() => {
    if (!planEnabled) {
      setLoading(false);
      return;
    }
    apiClient
      .get("/telephony/softphone")
      .then(({ data }) => { if (aliveRef.current) setConfig(data); })
      // A config we couldn't read is not an error the counsellor needs to see —
      // it just means phone mode, which is what they had yesterday.
      .catch(() => { if (aliveRef.current) setConfig({ enabled: false, mode: "phone" }); })
      .finally(() => { if (aliveRef.current) setLoading(false); });
  }, [planEnabled]);

  useEffect(loadConfig, [loadConfig]);

  const clearRetry = useCallback(() => {
    if (retryRef.current) {
      clearTimeout(retryRef.current);
      retryRef.current = null;
    }
  }, []);

  // ── Driver events ─────────────────────────────────────────────────────
  const handleEvent = useCallback((event) => {
    if (!aliveRef.current) return;

    switch (event.type) {
      case "registering":
        setStatus("registering");
        break;

      case "registered":
        attemptRef.current = 0;
        setStatus("registered");
        setReason("");
        // Health telemetry only — Settings shows "never registered" otherwise,
        // and a softphone that silently never connected is invisible.
        apiClient.post("/telephony/softphone/registered").catch(() => {});
        break;

      case "registration_failed":
        setStatus("failed");
        setReason(event.message || "Could not connect the softphone.");
        break;

      case "unregistered":
        setStatus("idle");
        setActiveCall(null);
        break;

      case "incoming":
      case "ringing":
        setActiveCall({ ...event.call, phase: event.type === "incoming" ? "incoming" : "ringing" });
        break;

      case "answered":
        setActiveCall((c) => ({ ...(c || {}), ...event.call, phase: "active", answeredAt: Date.now() }));
        break;

      case "ended":
        setActiveCall(null);
        break;

      case "error":
        setReason(event.message || "");
        break;

      default:
        break;
    }
  }, []);

  // ── Register / unregister ─────────────────────────────────────────────
  const register = useCallback(async () => {
    if (!config?.sipIdentity) return;
    if (driverRef.current) return;

    const epoch = ++epochRef.current;
    const superseded = () => epochRef.current !== epoch || !aliveRef.current;

    const driver = createSoftphoneDriver(config.provider);
    if (!driver) {
      setStatus("unavailable");
      setReason(`Browser calling isn't supported for ${config.providerLabel || "your provider"}.`);
      return;
    }

    const available = await driver.isAvailable();
    if (superseded()) return;   // nothing acquired yet, so nothing to release
    if (!available.ok) {
      // The commonest case in practice: the vendor SDK isn't deployed because the
      // account hasn't been provisioned yet. Phone mode, with the reason shown.
      setStatus("unavailable");
      setReason(available.reason || "Browser calling isn't available in this browser.");
      return;
    }

    driverRef.current = driver;
    const result = await driver.register({
      sipUri: config.sipUri,
      sipIdentity: config.sipIdentity,
      sipDomain: config.sipDomain,
      displayName: config.agentName,
      // A superseded driver must not narrate. Otherwise its late 'registered'
      // event flips the badge to "Ready" for a driver nobody holds a handle to,
      // so mute and hang-up do nothing.
      onEvent: (event) => { if (!superseded()) handleEvent(event); },
    });

    // Cancelled while in flight — almost always the counsellor switching back to
    // phone mode mid-registration. `unregister()` already ran and found the ref
    // empty, so nobody else is coming to clean this up: without the teardown
    // below the microphone stays open for the life of the tab, leaving the
    // browser's recording indicator on. That reads as being secretly listened to.
    if (superseded()) {
      if (driverRef.current === driver) driverRef.current = null;
      await driver.unregister().catch(() => {});
      return;
    }

    if (!result?.ok) {
      driverRef.current = null;
      // Keep retrying, with backoff. A counsellor who denied the mic once and
      // then allowed it should not have to reload the whole CRM.
      const delay = RETRY_MS[Math.min(attemptRef.current, RETRY_MS.length - 1)];
      attemptRef.current += 1;
      clearRetry();
      retryRef.current = setTimeout(() => {
        if (aliveRef.current) register();
      }, delay);
    }
  }, [config, handleEvent, clearRetry]);

  const unregister = useCallback(async () => {
    clearRetry();
    attemptRef.current = 0;
    // Synchronous, and first: it invalidates any register() still awaiting, so
    // that attempt releases its own microphone instead of orphaning it.
    epochRef.current += 1;
    const driver = driverRef.current;
    driverRef.current = null;
    await driver?.unregister().catch(() => {});
    setStatus("idle");
    setActiveCall(null);
  }, [clearRetry]);

  // Register when browser mode is on, tear down the moment it isn't. The cleanup
  // also covers logout, since the whole tree unmounts.
  useEffect(() => {
    if (wantsWebrtc) register();
    else unregister();
    return () => { unregister(); };
    // `register`/`unregister` are recreated when config changes, which is exactly
    // when the registration should be rebuilt.
  }, [wantsWebrtc, register, unregister]);

  // A tab that goes offline cannot receive a call. Say so and re-register on the
  // way back, rather than leaving a "Ready" badge on a softphone that is not.
  useEffect(() => {
    if (!wantsWebrtc) return undefined;
    const onOffline = () => {
      setStatus("failed");
      setReason("You're offline — calls will ring your phone until the connection is back.");
    };
    const onOnline = () => {
      attemptRef.current = 0;
      register();
    };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, [wantsWebrtc, register]);

  // ── Actions ───────────────────────────────────────────────────────────
  const answer = useCallback(() => driverRef.current?.answer(), []);
  const hangup = useCallback(() => driverRef.current?.hangup(), []);
  const sendDtmf = useCallback((digit) => driverRef.current?.sendDtmf(digit), []);

  // The driver call stays OUT of the state updater, reading the current call from
  // a ref instead. React invokes updaters twice under StrictMode, so a side
  // effect in there fires twice per click — harmless for an idempotent mute, but
  // it stops being harmless the moment a driver toggles rather than sets, and
  // `sendDtmf` in the same shape would dial a digit twice.
  const toggleMute = useCallback(() => {
    const current = activeCallRef.current;
    if (!current) return;
    const next = !current.muted;
    driverRef.current?.mute(next);
    setActiveCall((c) => (c ? { ...c, muted: next } : c));
  }, []);

  const toggleHold = useCallback(() => {
    const current = activeCallRef.current;
    if (!current) return;
    const next = !current.held;
    driverRef.current?.hold(next);
    setActiveCall((c) => (c ? { ...c, held: next } : c));
  }, []);

  /** Let the counsellor switch mode themselves, when the institute allows it. */
  const setPreferredMode = useCallback(async (mode) => {
    const { data } = await apiClient.put("/telephony/softphone", { preferredCallMode: mode });
    loadConfig();
    return data;
  }, [loadConfig]);

  /**
   * The mode the NEXT call should actually use.
   *
   * This is the fallback in one expression, and it is why a broken softphone
   * costs nobody a call: browser mode is claimed only while a registration is
   * genuinely live. Anything else — never registered, mic denied, SDK missing,
   * offline, mid-retry — dials the counsellor's phone instead.
   */
  const effectiveMode = wantsWebrtc && status === "registered" ? "webrtc" : "phone";

  return {
    loading,
    config,
    // 'idle' | 'registering' | 'registered' | 'failed' | 'unavailable'
    status,
    reason,
    activeCall,
    // True when a softphone should be visible at all: this agent has been given
    // an identity, even if it is currently switched off in favour of the phone.
    available: !!config?.enabled,
    mode: config?.mode || "phone",
    effectiveMode,
    canChooseMode: !!config?.allowAgentOverride && !!config?.enabled,
    answer,
    hangup,
    toggleMute,
    toggleHold,
    sendDtmf,
    setPreferredMode,
    reload: loadConfig,
    retry: () => { attemptRef.current = 0; register(); },
  };
}
